// Copyright 2026 AllSpice. All rights reserved.

import {SCHEMA_TYPES} from '../../features/allspicejson.ts';
import {ATTRIBUTES_DELIMITER, determineSchemaType, PCB_FIELDS, SCHEMATIC_FIELDS} from './diff-filter-shared.ts';

export type AttributeMapState = {
  attributesToDataIds: Record<string, string[]>;
  dataIdsToAttributes: Record<string, string[]>;
};

type JsonRecord = Record<string, unknown>;
type NullableJsonRecord = JsonRecord | null | undefined;

const SCHEMATIC_GRAPHICS_FIELDS = ['arcs', 'beziers', 'bitmaps', 'ellipses', 'polygons', 'polylines', 'rectangles', 'texts'];
const PCB_COMPONENT_INSTANCE_FIELDS = PCB_FIELDS;

const IGNORED_KEYS = new Set(['title', 'revision', 'variantname', 'documentname', 'html_id', 'font_id', 'net_id']);

function createAttributeMapState(): AttributeMapState {
  return {
    attributesToDataIds: {},
    dataIdsToAttributes: {},
  };
}

function isObjectLike(value: unknown): value is JsonRecord {
  return value !== null && typeof value === 'object';
}

function asObjectRecord(value: unknown): JsonRecord | null {
  return isObjectLike(value) && !Array.isArray(value) ? value : null;
}

function getOwnKeys(value: unknown): string[] {
  return isObjectLike(value) ? Object.keys(value) : [];
}

function getProperty(value: unknown, key: string): unknown {
  return isObjectLike(value) ? value[key] : undefined;
}

function hasMappedAttributes(mapState: AttributeMapState): boolean {
  return Object.keys(mapState.attributesToDataIds).length > 0;
}

function appendUniqueValues(target: Record<string, string[]>, key: string, values: string[]): void {
  if (values.length === 0) return;

  const merged = target[key] ? Array.from(target[key]) : [];
  const existingValues = new Set(merged);

  for (const value of values) {
    if (!existingValues.has(value)) {
      merged.push(value);
      existingValues.add(value);
    }
  }

  target[key] = merged;
}

function mergeRecordArrays(target: Record<string, string[]>, source: Record<string, string[]>): void {
  for (const [key, values] of Object.entries(source)) {
    appendUniqueValues(target, key, values);
  }
}

function mergeAttributeMapStates(target: AttributeMapState, source: AttributeMapState): void {
  mergeRecordArrays(target.attributesToDataIds, source.attributesToDataIds);
  mergeRecordArrays(target.dataIdsToAttributes, source.dataIdsToAttributes);
}

function getChangedDataIds(dataIdsToAttributes: Record<string, string[]>): string[] {
  return Object.keys(dataIdsToAttributes);
}

function mergeFieldFilterData(target: AttributeMapState, filterPath: string, source: AttributeMapState): void {
  if (!hasMappedAttributes(source)) return;

  mergeAttributeMapStates(target, source);
  appendUniqueValues(target.attributesToDataIds, filterPath, getChangedDataIds(source.dataIdsToAttributes));
}

/**
 * Recursively traverses base/head objects to identify changed attributes.
 */
export function getChangedAttributesRecursively(
  modifiedAttributes: Set<string>,
  baseAttrs: unknown,
  headAttrs: unknown,
  prefix = '',
): boolean {
  if (prefix === '') {
    const type = getProperty(headAttrs, 'type') ?? getProperty(baseAttrs, 'type');
    if (typeof type === 'string') {
      const changed = getChangedAttributesRecursively(modifiedAttributes, baseAttrs, headAttrs, type);
      if (changed) {
        modifiedAttributes.add(type);
      }
      return changed;
    }
  }

  if (baseAttrs === undefined || headAttrs === undefined) {
    modifiedAttributes.add(prefix);
    return true;
  }

  let changed = false;
  const isArray = Array.isArray(baseAttrs) || Array.isArray(headAttrs);

  for (const key of getOwnKeys(baseAttrs)) {
    if (IGNORED_KEYS.has(key)) continue;

    const pathSegment = isArray ? '*' : key;
    const path = prefix ? `${prefix}${ATTRIBUTES_DELIMITER}${pathSegment}` : pathSegment;
    const baseValue = getProperty(baseAttrs, key);
    const headValue = getProperty(headAttrs, key);

    if (headValue === undefined) {
      modifiedAttributes.add(path);
      changed = true;
      continue;
    }

    if (baseValue === headValue) {
      continue;
    }

    if (isObjectLike(baseValue) || isObjectLike(headValue)) {
      const childrenChanged = getChangedAttributesRecursively(modifiedAttributes, baseValue, headValue, path);
      if (childrenChanged) {
        modifiedAttributes.add(path);
        changed = true;
      }
      continue;
    }

    modifiedAttributes.add(path);
    changed = true;
  }

  for (const key of getOwnKeys(headAttrs)) {
    if (IGNORED_KEYS.has(key)) continue;

    const baseValue = getProperty(baseAttrs, key);
    if (baseValue === undefined) {
      const pathSegment = isArray ? '*' : key;
      const path = prefix ? `${prefix}${ATTRIBUTES_DELIMITER}${pathSegment}` : pathSegment;
      modifiedAttributes.add(path);
      changed = true;
    }
  }

  return changed;
}

function isObjectOrArray(value: unknown): boolean {
  return value !== null && typeof value === 'object';
}

/**
 * Builds a map of changed attributes to data IDs and the reverse mapping.
 */
export function buildAttributeMap(
  headData: NullableJsonRecord,
  baseData: NullableJsonRecord,
  prefix = '',
): AttributeMapState {
  const allDataIds = Array.from(new Set([
    ...Object.keys(headData ?? {}),
    ...Object.keys(baseData ?? {}),
  ])).filter((key) => !IGNORED_KEYS.has(key));

  const attributesToDataIds: Record<string, string[]> = {};
  const dataIdsToAttributes: Record<string, string[]> = {};

  for (const dataId of allDataIds) {
    const baseAttrs = baseData?.[dataId];
    const headAttrs = headData?.[dataId];

    if (!isObjectOrArray(baseAttrs) && !isObjectOrArray(headAttrs)) {
      continue;
    }

    const modifiedAttributes = new Set<string>();
    getChangedAttributesRecursively(modifiedAttributes, baseAttrs, headAttrs, prefix);

    const attributes = Array.from(modifiedAttributes);
    if (attributes.length === 0) continue;

    for (const attribute of attributes) {
      appendUniqueValues(attributesToDataIds, attribute, [dataId]);
    }
    dataIdsToAttributes[dataId] = attributes;
  }

  return {attributesToDataIds, dataIdsToAttributes};
}

/**
 * Processes the diff for a schematic document or page.
 */
export function processSchematic(
  headContents: NullableJsonRecord,
  baseContents: NullableJsonRecord,
  pageName: string,
): AttributeMapState {
  const result = createAttributeMapState();

  for (const field of SCHEMATIC_FIELDS) {
    const fieldName = pageName ? `${pageName}${ATTRIBUTES_DELIMITER}${field}` : field;
    const fieldData = buildAttributeMap(
      asObjectRecord(headContents?.[field]),
      asObjectRecord(baseContents?.[field]),
      fieldName,
    );
    mergeFieldFilterData(result, fieldName, fieldData);
  }

  const headGraphics = asObjectRecord(headContents?.['graphics']);
  const baseGraphics = asObjectRecord(baseContents?.['graphics']);
  if (headGraphics || baseGraphics) {
    for (const graphicsField of SCHEMATIC_GRAPHICS_FIELDS) {
      const fieldData = buildAttributeMap(
        asObjectRecord(headGraphics?.[graphicsField]),
        asObjectRecord(baseGraphics?.[graphicsField]),
        `graphics${ATTRIBUTES_DELIMITER}${graphicsField}`,
      );
      mergeFieldFilterData(result, 'graphics', fieldData);
    }
  }

  if (pageName) {
    const pageDataIds = getChangedDataIds(result.dataIdsToAttributes);
    if (pageDataIds.length > 0) {
      result.attributesToDataIds[pageName] = pageDataIds;
    }
  }

  return result;
}

function buildPcbFilterData(
  headContents: NullableJsonRecord,
  baseContents: NullableJsonRecord,
): AttributeMapState {
  const result = createAttributeMapState();
  const headComponentInstances = asObjectRecord(headContents?.['component_instances']);
  const baseComponentInstances = asObjectRecord(baseContents?.['component_instances']);
  const componentIds = new Set([
    ...Object.keys(headComponentInstances ?? {}),
    ...Object.keys(baseComponentInstances ?? {}),
  ]);

  for (const componentId of componentIds) {
    const componentFilterData = createAttributeMapState();
    const headComponent = asObjectRecord(headComponentInstances?.[componentId]);
    const baseComponent = asObjectRecord(baseComponentInstances?.[componentId]);

    for (const field of PCB_COMPONENT_INSTANCE_FIELDS) {
      const fieldName = `components${ATTRIBUTES_DELIMITER}${field}`;
      const fieldData = buildAttributeMap(
        asObjectRecord(headComponent?.[field]),
        asObjectRecord(baseComponent?.[field]),
        fieldName,
      );
      mergeFieldFilterData(componentFilterData, fieldName, fieldData);
    }

    mergeAttributeMapStates(result, componentFilterData);
  }

  if (hasMappedAttributes(result)) {
    result.attributesToDataIds.components = getChangedDataIds(result.dataIdsToAttributes);
  }

  for (const field of PCB_FIELDS) {
    const fieldData = buildAttributeMap(
      asObjectRecord(headContents?.[field]),
      asObjectRecord(baseContents?.[field]),
      field,
    );
    mergeFieldFilterData(result, field, fieldData);
  }

  return result;
}

export function buildFilterDataFromContents(
  headContents: NullableJsonRecord,
  baseContents: NullableJsonRecord,
  existingAttributesToDataIds: Record<string, string[]>,
): AttributeMapState {
  const schemaType = determineSchemaType(headContents, baseContents);

  if (schemaType === SCHEMA_TYPES.SCHEMATIC || schemaType === SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC) {
    const rawPageName = headContents?.['name'] ?? baseContents?.['name'];
    const pageName = schemaType === SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC || typeof rawPageName !== 'string' ? '' : rawPageName;

    let uniquePageName = pageName;
    let pageIndexer = 1;
    while (uniquePageName && existingAttributesToDataIds[uniquePageName]) {
      uniquePageName = `${pageName}_${pageIndexer}`;
      pageIndexer++;
    }

    return processSchematic(headContents, baseContents, uniquePageName);
  }

  if (schemaType === SCHEMA_TYPES.PCB) {
    return buildPcbFilterData(headContents, baseContents);
  }

  if (schemaType === SCHEMA_TYPES.ALTIUM_SCHEMATIC) {
    return buildAttributeMap(baseContents ?? {}, headContents ?? {});
  }

  throw new Error(`Unable to build filter tree for schema type ${schemaType}`);
}
